import React, { memo, useMemo } from 'react';

import { studentMessages } from '../../../../../translates/studentMessages';
import { trackingMessages } from '../../../../../translates/trackingMessages';
import { MenuContentWrapper } from '../../../../components/menu/ContentItems/MenuContentWrapper';
import { MenuItem as MenuItemComponent } from '../../../../components/menu/MenuItem';
import { TranslateResourceStringFnType, useIntlReexport } from '../../../../hooks/useIntlReexport';
import { Guid } from '../../../../store/model/guid';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { calculateIsLockedSelector } from '../../../RestrictMenu/calculateIsLocked';
import { MenuContentItem, TrackingStatus } from '@ael/core-api';
import {
  MenuItemType,
  ResourcesFolder,
  StudentItem,
  StudentResourceItem,
  StudentSubmenu,
  ProgressDisplayConfiguration,
} from '@ael/shared-components-library';
import { List } from 'immutable';

export enum ItemDetailsType {
  Item = 'Item',
  Submenu = 'Submenu',
  ResourcesFolder = 'ResourcesFolder',
  Resource = 'Resource',
}

export interface ItemDetails {
  active: boolean;
  nestedLevel: number;
  disabled: boolean;
  parentItemId: string | null;
  trackingStatus?: TrackingStatus;
  contentItems?: List<MenuContentItem>;
  progress?: ProgressDisplayConfiguration;
  showItemCompletion?: boolean;
  hideChevron?: boolean;
  itemId: Guid;
  itemDetailsType: ItemDetailsType.Item;
}

interface SubmenuDetails {
  active: boolean;
  hasActiveChild: boolean;
  disabled: boolean;
  parentItemId: string | null;
  nestedLevel: number;
  trackingStatus?: TrackingStatus;
  itemId: Guid;
  itemDetailsType: ItemDetailsType.Submenu;
}
interface ResourcesFolderDetails {
  active: boolean;
  hasActiveChild: boolean;
  parentItemId: string | null;
  nestedLevel: number;
  trackingStatus?: TrackingStatus;
  itemId: Guid;
  itemDetailsType: ItemDetailsType.ResourcesFolder;
}

interface ResourceDetails {
  active: boolean;
  parentItemId: string | null;
  trackingStatus?: TrackingStatus;
  contentItems?: List<MenuContentItem>;
  progress?: ProgressDisplayConfiguration;
  showItemCompletion?: boolean;
  hideChevron?: boolean;
  itemId: Guid;
  itemDetailsType: ItemDetailsType.Resource;
}

type Details = SubmenuDetails | ItemDetails | ResourcesFolderDetails | ResourceDetails;

export interface MenuItemStateProps {
  children?: React.ReactNode;
  menuItemDetails: Details;
  className?: string;
  elementId?: string;
  isLinearModeOn: boolean;
}

export interface MenuItemDispatchProps {
  onClick?: () => void;
  onMenuContentItemClick?: (contentItemId: string) => void;
}

export type MenuItemProps = MenuItemStateProps & MenuItemDispatchProps;

const useIsLocked = (itemId: Guid, isLinearModeOn: boolean): boolean => {
  const calculateIsLocked = useStudentSelector(calculateIsLockedSelector);

  return useMemo(() => calculateIsLocked(itemId, isLinearModeOn), [calculateIsLocked, itemId, isLinearModeOn]);
};

const extendMenuItemDetails = (
  menuItemDetails: Details,
  locked: boolean,
  formatMessage: TranslateResourceStringFnType,
  onMenuContentItemClick?: (contentItemId: string) => void
): StudentItem | StudentSubmenu | ResourcesFolder | StudentResourceItem => {
  switch (menuItemDetails.itemDetailsType) {
    case ItemDetailsType.Submenu:
      return { ...menuItemDetails, locked, menuItemType: MenuItemType.StudentSubmenu };
    case ItemDetailsType.ResourcesFolder:
      return { ...menuItemDetails, menuItemType: MenuItemType.ResourcesFolder };
    case ItemDetailsType.Resource:
      return { ...menuItemDetails, menuItemType: MenuItemType.Resource };
    default: {
      const contentItemsComponent = menuItemDetails.contentItems ? (
        <MenuContentWrapper
          contentItems={menuItemDetails.contentItems}
          active={menuItemDetails.active}
          onContentItemClick={onMenuContentItemClick}
          nestedLevel={menuItemDetails.nestedLevel}
        />
      ) : undefined;

      return {
        ...menuItemDetails,
        locked,
        menuItemType: MenuItemType.StudentItem,
        ContentItemsComponent: contentItemsComponent,
        messages: {
          emptyItemText: formatMessage(studentMessages.emptyItem),
          completionLabelText: formatMessage(trackingMessages.completed),
          optionalItemText: formatMessage(trackingMessages.optional),
          passedText: formatMessage(trackingMessages.passed),
          incompleteText: formatMessage(trackingMessages.notPassed),
          notStartedText: formatMessage(trackingMessages.notStarted),
        },
      };
    }
  }
};

export const MenuItem: React.FC<MenuItemProps> = memo(
  ({ children, menuItemDetails, className, elementId, isLinearModeOn, onClick, onMenuContentItemClick }) => {
    const isLocked = useIsLocked(menuItemDetails.itemId, isLinearModeOn);
    const { formatMessage } = useIntlReexport();

    const extendedDetails = useMemo(
      () => extendMenuItemDetails(menuItemDetails, isLocked, formatMessage, onMenuContentItemClick),
      [menuItemDetails, onMenuContentItemClick, isLocked, formatMessage]
    );

    return (
      <MenuItemComponent
        menuItemDetails={extendedDetails}
        className={className}
        onClick={onClick}
        elementId={elementId}
      >
        {children}
      </MenuItemComponent>
    );
  }
);
